import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { useFeathers } from '../../app/util';
import {
  CirclePhoto,
  SearchableSelectInput
} from '..';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import moment from 'moment';

const candidatesFromRace = (race) => {
  if(race?.candidates?.length) return race.candidates;
  return [];
};

const UpdateRaceElection = ({
  onError,
  onSuccess,
  populateOnStart = false,
  race,
  redirectOnFinish,
}) => {
  const feathers = useFeathers();
  const history = useHistory();

  const electionOldKey = race?.election;
  const raceId = race?._id || race;
  const [ electionOld, setElectionOld ] = useState(null);
  const [ formInputs, setFormInputs ] = useState({
    election: null,
  })

  const [submitting, setSubmitting] = useState(false);
  const [rollInConflict, setRollInConflict] = useState(null);
  const [rollInTargetRace, setRollInTargetRace] = useState(null);
  const [rollInTargetRaceLoading, setRollInTargetRaceLoading] = useState(false);

  const submittable = formInputs.election && formInputs.election?.key !== electionOldKey && !submitting;

  useEffect(() => {
    if(feathers && electionOldKey && !formInputs.election && populateOnStart) loadInitialElection()
  }, [feathers, electionOldKey, populateOnStart]);
  
  const electionOptionFromElection = (election) => {
    return {
      text: election.name,
      subText: moment(election.date).format('LL'),
      key: election.key,
      electionObj: election
    }
  }
  
  const loadInitialElection = async () => {
    const election = await feathers.getService('elections').get(electionOldKey);

    setElectionOld(election);
    setFormInputs({ election: electionOptionFromElection(election)})
  }


  const onChange = e => {
    const { name, value } = e.target;
    const update = {};
    update[name] = value;
    let newInputs = {
      ...formInputs,
      ...update
    };
    
    setFormInputs(newInputs)
  }

  /* manage searching options for selection, based on search text */
  const [ optionsForField, setOptionsForField ] = useState({});
  const [loadingForField, setLoadingForField] = useState({});
  
  const searchForField = async (field, searchText, addtlParams) => {
    if(!feathers) return;

    const loadingUpdate = {};
    loadingUpdate[field] = true;
    setLoadingForField({ ...loadingForField, ...loadingUpdate });

    let options = [];
      if (field === 'election') {
      const query = {
        state: electionOld?.state,
        electionType: 'general',
        status: ['prep', 'live'],
        $skip: 0,
        $limit: 19,
        ...(searchText
          ? { name: { $search: searchText } }
          : {}
        ),
        $sort: { date: -1 }
      }
      const elections = await feathers.getService('elections').find({ query });
      options = elections.data.map((r) => (electionOptionFromElection(r)))
    }

    const updateForOptions = {};
    updateForOptions[field] = options;
    setOptionsForField({ ...optionsForField, ...updateForOptions })

    const loadingUpdate2 = {};
    loadingUpdate2[field] = false;
    setLoadingForField({ ...loadingForField, ...loadingUpdate2 });

    return options;
  }

  const migrateRace = async ({ rollInWithRace } = {}) => {
    const targetElectionKey = formInputs.election.key;
    const patchOp = await feathers.getService('races').patch(raceId, {
      election: targetElectionKey,
      ...(rollInWithRace ? { rollInWithRace } : {})
    }, {
      query: {
        migrateRace: true
      }
    })

    if (redirectOnFinish) {
      history.push(`/elections/${targetElectionKey}/races/${rollInWithRace || raceId}`)
    }

    if(onSuccess) onSuccess(patchOp)
  }

  const conflictDataFromError = err => err?.data || err?.response?.body?.data || {};

  const loadRollInTargetRace = async (targetRaceId) => {
    setRollInTargetRace(null);
    setRollInTargetRaceLoading(true);
    try {
      const targetRace = await feathers.getService('races').get(targetRaceId, {
        query: {
          $populate: 'candidates'
        }
      });
      setRollInTargetRace(targetRace);
    } catch(e) {
      if(onError) onError(e);
    } finally {
      setRollInTargetRaceLoading(false);
    }
  }

  const raceDistrictName = race?.originalDistrict?.longName ||
    race?.district?.longName ||
    race?.district?.name ||
    race?.district ||
    '--';

  const positionDataText = useMemo(() => {
    const positionData = race?.positionData || {};
    const parts = [];

    if(positionData.seatIdentifierType || positionData.seatIdentifier) {
      parts.push(`${positionData.seatIdentifierType || 'Seat'} ${positionData.seatIdentifier || ''}`.trim());
    }
    if(positionData.special) parts.push('Special election');

    return parts.length ? parts.join(', ') : '--';
  }, [race]);

  const candidatesForModal = useMemo(() => candidatesFromRace(race), [race]);
  const existingCandidatesForModal = useMemo(() => candidatesFromRace(rollInTargetRace), [rollInTargetRace]);

  const submit = async () => {
    if(!submittable || submitting) return;
    setSubmitting(true)
    try {
      await migrateRace()
    } catch(e) {
      const conflictData = conflictDataFromError(e);
      if(e?.code === 409 && conflictData?.code === 'race-migration-roll-in-required' && conflictData?.targetRaceId) {
        setRollInConflict(conflictData);
        loadRollInTargetRace(conflictData.targetRaceId);
      } else if(onError) {
        onError(e)
      }
      setSubmitting(false)
    }
  }

  const confirmRollIn = async () => {
    if(!rollInConflict?.targetRaceId || submitting) return;
    setSubmitting(true);
    try {
      await migrateRace({ rollInWithRace: rollInConflict.targetRaceId });
      setRollInConflict(null);
    } catch(e) {
      if(onError) onError(e);
      setSubmitting(false);
    }
  }

  const cancelRollIn = () => {
    if(submitting) return;
    setRollInConflict(null);
    setRollInTargetRace(null);
  }

  return (
    <Wrapper>
      <Top>
        <Row>
          <div className='label'>Election</div>
          <SearchableSelectInput
            name='election'
            onChangeSearchText={(searchText) => searchForField('election', searchText)}
            value={formInputs.election}
            onChange={onChange}
            optionsLoading={loadingForField.election}
            options={optionsForField?.election || []}
            style={{ flexGrow: 1, width: '100%' }}
          />
        </Row>
      </Top>
      <Button
        size='small'
        disabled={!submittable || submitting}
        onClick={submit}
        variant='contained'
        color='primary'
        style={{ alignSelf: 'flex-end'}}
      >
        {submitting ? `Updating...` : 'Update'}
      </Button>
      <Dialog
        open={Boolean(rollInConflict)}
        onClose={cancelRollIn}
      >
        <DialogTitle>Roll into existing race?</DialogTitle>
        <DialogContent>
          <DialogIntro>
            This race matches an existing race in the selected election. If you continue, the candidates below
            will be moved into that existing race and this race will be deleted. This cannot be undone.
          </DialogIntro>
          <RaceDetails>
            <DetailRow>
              <DetailLabel>Office</DetailLabel>
              <Typography variant='body1'>{race?.officeName || race?.office || '--'}</Typography>
            </DetailRow>
            <DetailRow>
              <DetailLabel>District</DetailLabel>
              <Typography variant='body1'>{raceDistrictName}</Typography>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Position</DetailLabel>
              <Typography variant='body1'>{positionDataText}</Typography>
            </DetailRow>
            <ExistingCandidatesBlock>
              <DetailLabel>Existing candidates</DetailLabel>
              {
                rollInTargetRaceLoading
                ? <Typography variant='body2' color='textSecondary'>Loading candidates...</Typography>
                : (
                  existingCandidatesForModal.length
                  ? <CandidateList>
                    {existingCandidatesForModal.map(candidate => (
                      <CandidateRow key={candidate._id || candidate.name}>
                        <CirclePhoto
                          size='small'
                          photoPath={candidate.stagingDraft?.photoPathFace || candidate.photoPathFace}
                          hasVariants={(candidate.stagingDraft?.photoPathFace ? candidate.stagingDraft?.hasVariants : candidate.hasVariants) === true}
                        />
                        <CandidateText>
                          <Typography variant='body1'>{candidate.stagingDraft?.name || candidate.name || 'Unnamed candidate'}</Typography>
                          <Typography variant='body2' color='textSecondary'>{candidate.party || 'No party'}</Typography>
                        </CandidateText>
                      </CandidateRow>
                    ))}
                  </CandidateList>
                  : <Typography variant='body2' color='textSecondary'>No existing candidates found on the target race.</Typography>
                )
              }
            </ExistingCandidatesBlock>
          </RaceDetails>
          <CandidatesTitle variant='subtitle2'>Candidates to move</CandidatesTitle>
          <CandidateList>
            {
              candidatesForModal.length
              ? candidatesForModal.map(candidate => (
                <CandidateRow key={candidate._id || candidate.name}>
                  <CirclePhoto
                    size='small'
                    photoPath={candidate.stagingDraft?.photoPathFace || candidate.photoPathFace}
                    hasVariants={(candidate.stagingDraft?.photoPathFace ? candidate.stagingDraft?.hasVariants : candidate.hasVariants) === true}
                  />
                  <CandidateText>
                    <Typography variant='body1'>{candidate.stagingDraft?.name || candidate.name || 'Unnamed candidate'}</Typography>
                    <Typography variant='body2' color='textSecondary'>{candidate.party || 'No party'}</Typography>
                  </CandidateText>
                </CandidateRow>
              ))
              : <Typography variant='body2' color='textSecondary'>No candidates found on this race.</Typography>
            }
          </CandidateList>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRollIn} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={confirmRollIn} color='primary' variant='contained' disabled={submitting}>
            {submitting ? 'Rolling in...' : 'Roll Into Existing Race'}
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`
const Top = styled.div`
  width: 100%;
`;


const Row = styled.div`
  align-items: center;
  margin-right: 4px;
  width: 100%;
  margin-bottom: 12px;

  div.label {
    opacity: 0.7;
    font-size: 14px;
    ${props => props.theme.font.normal}
  }
`;

const DialogIntro = styled(Typography).attrs({
  variant: 'body1'
})`
  margin-bottom: 16px;
`;

const RaceDetails = styled.div`
  border: solid 1px #DDDDDD;
  border-radius: 8px;
  padding: 12px;
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 12px;
  align-items: start;
`;

const DetailLabel = styled.div`
  opacity: 0.7;
  font-size: 14px;
  ${props => props.theme.font.normal}
`;

const DetailDivider = styled.div`
  border-top: solid 1px #DDDDDD;
  margin: 2px 0;
`;

const ExistingCandidatesBlock = styled.div`
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 12px;
  align-items: start;
`;

const CandidatesTitle = styled(Typography)`
  margin: 4px 0 8px;
`;

const CandidateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CandidateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CandidateText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;


export { UpdateRaceElection };
